import { useEffect, useState } from "react";
import { Sparkles, Plus, Check } from "lucide-react";
import { suggestServices } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { locItem, money } from "@/components/campaign/ServiceCatalog";

export const useSuggestions = (audience, analysisId) => {
  const [s, setS] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => suggestServices({ audience: audience || 0, analysis_id: analysisId || undefined })
      .then((d) => setS(d.items)).catch(() => setS([])), 350);
    return () => clearTimeout(t);
  }, [audience, analysisId]);
  return s;
};

const reasonText = (s, tr) => {
  const n = s.n != null ? Number(s.n).toLocaleString("de-CH") : "";
  return {
    traffic: tr("Porta traffico qualificato sul sito analizzato", "Brings qualified traffic to the analysed site"),
    big_audience: `${tr("Pubblico ampio", "Large audience")} (~${n}): ${tr("contatti verificati e invio email", "verified contacts and email sending")}`,
    niche: tr("Pubblico di nicchia: lo scraping certificato trova anche i contatti più difficili", "Niche audience: certified scraping finds even the hardest contacts"),
    seo: `${tr("Punteggio SEO", "SEO score")} ${n}/100: ${tr("più visibilità su motori di ricerca e directory", "more visibility on search engines and directories")}`,
    tracking: tr("Pochi strumenti di tracciamento: lascia gestire a noi Google ADV, Meta e WhatsApp", "Few tracking tools: let us manage Google ADV, Meta and WhatsApp"),
    social: tr("Rafforza la fiducia con profili social in crescita", "Build trust with growing social profiles"),
  }[s.reason] || "";
};

export const SuggestedServices = ({ suggestions, items, cart, setQty }) => {
  const { lang, tr } = useLang();
  const byId = Object.fromEntries((items || []).map((i) => [i.id, i]));
  const list = suggestions.filter((s) => byId[s.id]);
  if (!list.length) return null;
  const missing = list.filter((s) => !cart[s.id]);
  const total = list.reduce((a, s) => a + byId[s.id].price, 0);
  return (
    <div data-testid="suggested-services" className="max-w-5xl mb-8 border border-[#cfd6d9] bg-white p-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-[#E0262C]"><Sparkles className="w-3.5 h-3.5" /> {tr("Proposta AI per la tua campagna", "AI proposal for your campaign")}</div>
          <p className="text-xs text-[#5b6168] mt-1">{tr("In base alla tua analisi e al pubblico stimato. È solo un suggerimento: scegli liberamente.", "Based on your analysis and estimated audience. It's only a suggestion: choose freely.")}</p>
        </div>
        <button type="button" data-testid="suggest-add-all" disabled={!missing.length} onClick={() => missing.forEach((s) => setQty(s.id, 1))}
          className="px-4 py-2 text-xs font-medium rounded-full border border-[#2B3036] hover:bg-[#2B3036] hover:text-white disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[#2B3036] transition-colors">
          {missing.length ? `${tr("Aggiungi tutti", "Add all")} · ${money(total)}` : tr("Tutti nel carrello", "All in cart")}
        </button>
      </div>
      <ul className="divide-y divide-[#e4e4e7]">
        {list.map((s) => {
          const it = locItem(byId[s.id], lang);
          const on = !!cart[s.id];
          return (
            <li key={s.id} data-testid={`suggest-item-${s.id}`} className="py-2.5 flex items-center gap-3">
              <span className="font-mono text-[10px] text-[#a1a1aa] w-6">{it.code}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.name}</div>
                <div className="text-[11px] text-[#71717a]">{reasonText(s, tr)}</div>
              </div>
              <span className="font-mono text-xs">{money(it.price)}</span>
              <button type="button" data-testid={`suggest-add-${s.id}`} disabled={on} onClick={() => setQty(s.id, 1)}
                className={`w-8 h-8 flex items-center justify-center border transition-colors ${on ? "border-[#10b981] text-[#10b981]" : "border-[#2B3036] hover:bg-[#2B3036] hover:text-white"}`}>
                {on ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
